// Article — first-class content kind. The shape mirrors the Drizzle row
// in `packages/db/src/schema/content/articles.ts` so the wire response
// can be derived from a row + a small projection step (see
// `apps/api/src/modules/content/articles/service.ts::toArticleDto`).
//
// Every column on the DB side appears here in some form; flag-gated /
// reserved columns (paywall amount, password hash, current_version_id)
// are omitted from the public DTO entirely (DB-only — the API never
// surfaces them) so a leaked row shape can't expose pricing or hashes.
import { z } from 'zod';
export const ArticleHttpUrl = z
    .string()
    .url()
    .regex(/^https?:\/\/[^\s<>"'`]+$/, 'URL must use http or https')
    .max(2048);
// ---------------------------------------------------------------------------
// Closed enums — these MUST stay in sync with the CHECK constraints in
// migration 0005 and with the consts in `packages/db/src/schema/content/articles.ts`.
// ---------------------------------------------------------------------------
export const ArticleStatus = z.enum([
    'draft',
    'scheduled',
    'published',
    'unpublished',
    'archived',
]);
export const ArticleAccessMode = z.enum([
    'public',
    'logged-in',
    'private',
    'group',
    'password',
    'paywall',
    'allowlist',
]);
export const ArticleTranslationLocale = z.enum(['zh', 'en']);
export const ArticleTranslationSourceLocale = z.enum(['zh', 'en', 'mixed']);
export const ArticleTranslationStatus = z.enum(['pending', 'completed', 'failed']);
export const ArticleCollaboratorTargetType = z.enum(['user', 'group']);
export const ArticleCollaboratorRole = z.enum(['viewer', 'commenter', 'editor']);
export const ArticleViewerRole = z.enum([
    'owner',
    'manager',
    'collaborator-viewer',
    'collaborator-commenter',
    'collaborator-editor',
]);
export const ArticleWechatThemeDefinition = z
    .object({
    palette: z.record(z.string(), z.string()).optional(),
    typography: z.record(z.string(), z.unknown()).optional(),
    spacing: z.record(z.string(), z.unknown()).optional(),
    blocks: z.record(z.string(), z.unknown()).optional(),
    customCss: z.string().max(20_000).optional(),
})
    .passthrough();
export const ArticleTranslationSummary = z.object({
    targetLocale: ArticleTranslationLocale,
    sourceLocale: ArticleTranslationSourceLocale,
    status: ArticleTranslationStatus,
    updatedAt: z.string().datetime({ offset: true }),
    completedAt: z.string().datetime({ offset: true }).nullable(),
});
export const ArticleBlock = z.lazy(() => z
    .object({
    id: z.string().min(1),
    type: z.string().min(1),
    content: z.string(),
    html: z.string().optional(),
    attributes: z.record(z.string(), z.unknown()),
    children: z.array(ArticleBlock).optional(),
    meta: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough());
export const DOMEditorArticleBlock = z.lazy(() => z
    .object({
    id: z.string().min(1),
    type: z.string().min(1),
    attrs: z.record(z.string(), z.unknown()),
    content: z.array(z.unknown()).optional(),
    children: z.array(DOMEditorArticleBlock).optional(),
})
    .passthrough());
export const DOMEditorArticleBody = z
    .object({
    version: z.literal(2),
    blocks: z.array(DOMEditorArticleBlock),
    meta: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough();
export const LegacyArticleBody = z.array(ArticleBlock);
export const ArticleBody = z.union([LegacyArticleBody, DOMEditorArticleBody]);
// ---------------------------------------------------------------------------
// SEO bundle — flat for ergonomics. Every field is optional because new
// articles default to "use the title for everything" and we don't want
// to force authors through an SEO form on first save.
// ---------------------------------------------------------------------------
export const ArticleSeo = z.object({
    seoTitle: z.string().max(160).nullable(),
    seoDescription: z.string().max(320).nullable(),
    canonicalUrl: ArticleHttpUrl.nullable(),
    ogTitle: z.string().max(160).nullable(),
    ogDescription: z.string().max(320).nullable(),
    // `ogImageUrl` was dropped: the writer panel uses `coverImageUrl`
    // as the canonical Open Graph share-card image (one upload, not
    // two). Reader/share-card renderers should fall back to the
    // article's cover.
    noindex: z.boolean(),
    nofollow: z.boolean(),
});
// ---------------------------------------------------------------------------
// ArticleTag — denormalised tag projection inlined on `Article.tags[]`.
//
// Mirrors `apps/api/src/modules/content/articles/dto.ts::ArticleTagDto`.
// The full Tag schema (with `archivedAt`, `createdAt`, `updatedAt`) lives
// next to the tags-route contracts; this trimmed shape is what the
// articles route layer surfaces on the article DTO so detail pages can
// render tag chips without a second round-trip.
// ---------------------------------------------------------------------------
export const ArticleTag = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    color: z.string(),
});
// ---------------------------------------------------------------------------
// Article — canonical wire shape.
// ---------------------------------------------------------------------------
export const Article = z.object({
    id: z.string().uuid(),
    slug: z.string().regex(/^[a-z0-9](-?[a-z0-9])*$/).min(1).max(96),
    authorId: z.string().uuid(),
    title: z.string().min(1).max(280),
    /** Hand-written or auto-derived excerpt. Empty string when neither. */
    summary: z.string(),
    /** Legacy block array or DOM-Editor `{ version: 2, blocks }` document. */
    body: ArticleBody,
    /** Flat-text projection of `body` for search / preview. */
    bodyText: z.string(),
    coverImageUrl: ArticleHttpUrl.nullable(),
    collaborationEnabled: z.boolean(),
    yjsHash: z.string().nullable(),
    contentFromYjsAt: z.string().datetime({ offset: true }).nullable(),
    collaborationUpdatedAt: z.string().datetime({ offset: true }).nullable(),
    viewerArticleRole: ArticleViewerRole.nullable(),
    collaborationRole: ArticleCollaboratorRole.nullable(),
    isCollaborationArticle: z.boolean(),
    /** AI-generated translations available for this article. Full
     *  translated bodies are fetched from `/articles/:slug/translations/:locale`
     *  so the main detail response stays close to the original article row. */
    translations: z.array(ArticleTranslationSummary),
    // ---- lifecycle ----
    status: ArticleStatus,
    /** Set by `/publish` (instant or scheduled). For status='scheduled' the
     *  value is in the future; for 'published' it is in the past or now. */
    publishedAt: z.string().datetime({ offset: true }).nullable(),
    /** Auto-unpublish deadline. The `articles.scheduled-unpublish` cron
     *  flips `status='published' AND scheduledUnpublishAt<=now` rows to
     *  `unpublished` and consumes (clears) the column atomically. Settable
     *  via Create / Update; clear by sending `null`. */
    scheduledUnpublishAt: z.string().datetime({ offset: true }).nullable(),
    unpublishedAt: z.string().datetime({ offset: true }).nullable(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    // ---- visibility ----
    accessMode: ArticleAccessMode,
    access: z.object({
        mode: ArticleAccessMode,
        locked: z.boolean(),
        passwordUnlocked: z.boolean(),
        paywallAmountCents: z.number().int().positive().nullable(),
        paywallCurrency: z.string().regex(/^[A-Z]{3}$/).nullable(),
        paid: z.boolean(),
        allowlisted: z.boolean().nullable(),
        allowlistUserIds: z.array(z.string().uuid()).nullable(),
    }),
    /** Group attachments live in `resource_groups` (kind='article'). The
     *  list is denormalised onto the DTO for ergonomics — clients render
     *  visibility chips without a second round-trip. */
    groupIds: z.array(z.string().uuid()),
    // ---- taxonomy ----
    /** Optional category attachment. Validated to exist + not be archived
     *  by the service layer. `null` when the article is uncategorised. */
    categoryId: z.string().uuid().nullable(),
    /** Tag attachments via `article_tags`. Inlined as a denormalised
     *  projection so the detail route doesn't need a second round-trip;
     *  list endpoints intentionally project this away (see ArticleSummary)
     *  to avoid the per-row N+1 join. Sorted by tag name ASC for stable
     *  display. */
    tags: z.array(ArticleTag),
    // ---- SEO ----
    seo: ArticleSeo,
    // ---- viewer-relative annotation ----
    /** Whether the requesting viewer can edit (author or manager+). Set by
     *  the route layer; null in viewer-less contexts (activity payloads,
     *  cron jobs). */
    viewerCanWrite: z.boolean().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
/** A trimmed projection used for list/feed surfaces — body / bodyText
 *  stripped (heavy) AND tags stripped (loading them per row would N+1
 *  the list query; clients fetching tags hit the detail endpoint). The
 *  summary therefore exposes `categoryId` but not the resolved tag set. */
export const ArticleSummary = Article.omit({
    body: true,
    bodyText: true,
    tags: true,
    translations: true,
});
//# sourceMappingURL=article.js.map